/**
 * `<SideDrawer/>` — the non-modal right-edge drawer (#1920).
 *
 * The founder's 2 Oct brief: a panel that comes out at the side must not
 * black out or shrink the page — it slides in OVER the page's right edge,
 * and the page behind keeps its width, stays scrollable and stays
 * clickable, because the page is what the user decides against. Two
 * consumers share this one primitive:
 *
 *   - the lead's next-steps panel (`<PanelAwareLayout/>` in AppShell, R1+R2
 *     of `docs/agent-prompts/handover-right-panel-and-inbox-2026-10-02.md`);
 *   - the roster's `?surface=` decision panels (R6 — the ladder, Schedule
 *     dates, Coverage, Swaps …), at the wider `lg` / `xl` widths.
 *
 * Built on the same Radix dialog as `ui/Dialog` / `ui/BottomSheet`, but in
 * Radix's NON-modal mode, and that is the whole point:
 *
 *   - `modal={false}` — no focus trap, no `aria-hidden` on the rest of the
 *     page, no `pointer-events: none` on `<body>`, no scroll lock (Radix
 *     only applies those in modal mode, and the scroll lock lives on the
 *     `Overlay`, which this drawer never renders). No scrim either.
 *   - `onInteractOutside` is prevented: a click (or a focus move) on the
 *     page behind never closes the drawer — the user is meant to work the
 *     page with the drawer open. It closes on its ✕, `Esc`, or whatever
 *     toggle the consumer owns (the panel handle, a surface chip).
 *   - `role="dialog"` comes from Radix; non-modal Radix sets no
 *     `aria-modal`, which is correct for a panel the page stays live
 *     beside.
 *   - Focus moves into the drawer on open (Radix's default) and back to
 *     the element that opened it on close — but only when focus was inside
 *     the drawer (a user who clicked into the page and then pressed `Esc`
 *     keeps their place). Pass `returnFocusRef` to name the opener
 *     explicitly (Safari does not focus a clicked button).
 *   - Never `<body>` (#1934). A drawer opened by a deep link (`?surface=`,
 *     `?date=…&ladder=1`) has no opener — focus sat on `<body>` when it
 *     opened. On close the first of these that is still on the page wins:
 *     `returnFocusRef`, the opener, `fallbackFocusRef` (the control that
 *     opens this drawer, named by the consumer), then the page's main
 *     landmark (`appshell-main`, made focusable for that one hand-off).
 *
 * Geometry: `fixed right-0 top-0 h-dvh`, `z-40` (over the sticky app
 * header, under toasts), a fixed width per `width` (`md` 360 · `lg` 480 ·
 * `xl` 720 px, never wider than the viewport), its own vertical scroll
 * under a sticky header. Slides in from the right edge and back out on
 * close (`motion-drawer`, styles/motion-shell.css — #2172);
 * `prefers-reduced-motion` lands it at once.
 *
 * The body carries no padding of its own: the content brings it (the
 * next-steps panel keeps its `p-3`).
 */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { type ReactNode, type RefObject, useEffect, useRef } from "react";

import { cn } from "../../lib/cn";
import { registerSidePanel } from "../../lib/sidePanels";

export type SideDrawerWidth = "md" | "lg" | "xl";

/** Literal class strings (Tailwind compiles them as written). */
const WIDTH_CLASS: Record<SideDrawerWidth, string> = {
  md: "w-[360px]",
  lg: "w-[480px]",
  xl: "w-[720px]",
};

/** The drawer widths in px — for a consumer that positions something
 *  against the drawer's left edge (the panel handle does). */
export const SIDE_DRAWER_WIDTH_PX: Record<SideDrawerWidth, number> = {
  md: 360,
  lg: 480,
  xl: 720,
};

export interface SideDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Visible title — also the dialog's accessible name. */
  title: ReactNode;
  /** Optional one-line description under the title (wired as
   *  `aria-describedby`). */
  description?: ReactNode;
  /** Accessible name when the title holds controls (an editable title):
   *  it replaces the name read from the title. */
  ariaLabel?: string | undefined;
  /** `md` 360 px (default) · `lg` 480 px · `xl` 720 px. */
  width?: SideDrawerWidth | undefined;
  /** `data-testid` for the drawer; its scrolling body gets `${testid}-body`. */
  testid?: string | undefined;
  /** Extra controls in the header, left of the ✕ (a filter, a link). */
  headerExtra?: ReactNode;
  /** Extra classes on the drawer itself. */
  className?: string | undefined;
  /** Where focus returns on close. Defaults to the element that held focus
   *  when the drawer opened. */
  returnFocusRef?: RefObject<HTMLElement | null> | undefined;
  /** Where focus goes on close when neither `returnFocusRef` nor the opener
   *  is still on the page (a deep-link open has no opener): the control
   *  that opens this drawer. The main landmark is the last resort. */
  fallbackFocusRef?: RefObject<HTMLElement | null> | undefined;
  /** #2833 — a soft sheet: rounded edge, a tinted header band with a small
   *  line above the title and art on the right. Without it the drawer is
   *  the plain one every other consumer uses. */
  soft?: { kicker?: ReactNode; sub?: ReactNode; art?: ReactNode } | undefined;
  /** #2867 — an eyebrow header: the title as a small-caps line (no bar, no
   *  border) and a "Close" text control on the right. */
  eyebrow?: boolean | undefined;
  children: ReactNode;
}

/** A focus target that is still on the page — never `<body>`. */
function isUsableFocusTarget(el: HTMLElement | null | undefined): el is HTMLElement {
  return Boolean(el?.isConnected) && el !== document.body;
}

/**
 * The page's main landmark: the last place focus can return to. It is not
 * focusable by default (a permanent `tabIndex={-1}` would make every click
 * on the page focus it), so it is made focusable for this one hand-off and
 * goes back to unfocusable when focus leaves it — the skip-link pattern.
 */
function mainLandmark(): HTMLElement | null {
  const main =
    document.querySelector<HTMLElement>('[data-testid="appshell-main"]') ??
    document.querySelector<HTMLElement>("main");
  if (main && !main.hasAttribute("tabindex")) {
    main.setAttribute("tabindex", "-1");
    main.addEventListener("blur", () => main.removeAttribute("tabindex"), { once: true });
  }
  return main;
}

export function SideDrawer({
  open,
  onOpenChange,
  title,
  description,
  ariaLabel,
  width = "md",
  testid,
  headerExtra,
  className,
  returnFocusRef,
  fallbackFocusRef,
  soft,
  eyebrow,
  children,
}: SideDrawerProps) {
  // The element that held focus when the drawer opened. Radix returns
  // focus to a `Dialog.Trigger`, but this drawer's openers live outside
  // it (the panel handle, a roster chip), so it remembers the opener
  // itself.
  const openerRef = useRef<HTMLElement | null>(null);

  // #2824 - Autoplay closes open panels before it opens the demo phone.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    if (!open) return;
    return registerSidePanel(() => onOpenChangeRef.current(false));
  }, [open]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogPrimitive.Portal>
        {/* No `Overlay`: no scrim, no scroll lock — the page stays live. */}
        <DialogPrimitive.Content
          data-testid={testid}
          data-width={width}
          className={cn(
            "fixed right-0 top-0 z-40 flex h-dvh max-w-[100vw] flex-col overflow-y-auto",
            soft
              ? "rounded-l-3xl bg-bg-elevated shadow-xl focus:outline-none"
              : "border-l border-border bg-bg-elevated shadow-lg focus:outline-none",
            // #2172 — slides in from the right edge and back out on close.
            "motion-drawer",
            WIDTH_CLASS[width],
            className,
          )}
          // Radix warns when a dialog has no Description; opt out
          // explicitly when the caller gave none.
          {...(description || soft?.kicker ? {} : { "aria-describedby": undefined })}
          {...(ariaLabel ? { "aria-label": ariaLabel, "aria-labelledby": undefined } : {})}
          onOpenAutoFocus={() => {
            const active = typeof document === "undefined" ? null : document.activeElement;
            openerRef.current = active instanceof HTMLElement ? active : null;
          }}
          onCloseAutoFocus={(event) => {
            // Take over from Radix (which only knows a `Dialog.Trigger`).
            event.preventDefault();
            const active = document.activeElement;
            // Focus left with the drawer (it falls to <body>) → hand it
            // back to the opener. If the user had moved into the page, stay.
            if (active && active !== document.body) return;
            // The first target still on the page (#1934 — never <body>):
            // the named opener, the element focused at open, the control
            // that opens this drawer, then the main landmark.
            const target =
              [returnFocusRef?.current, openerRef.current, fallbackFocusRef?.current].find(
                isUsableFocusTarget,
              ) ?? mainLandmark();
            target?.focus();
          }}
          // The page behind is meant to be used with the drawer open: a
          // click or focus move out there must never close it.
          onInteractOutside={(event) => event.preventDefault()}
        >
          {soft ? (
            <div
              className="sticky top-0 z-10 min-h-[132px] shrink-0 bg-accent-soft px-[30px] py-6 [background-image:radial-gradient(80%_140%_at_75%_20%,rgb(var(--color-bg-elevated)/0.45),transparent_70%)]"
              data-testid={testid ? `${testid}-band` : undefined}
            >
              {soft.kicker || description ? (
                <DialogPrimitive.Description className="max-w-[62%] text-[13px] text-fg-muted">
                  {soft.kicker}
                  {description ? <span className="block">{description}</span> : null}
                </DialogPrimitive.Description>
              ) : null}
              <DialogPrimitive.Title className="mt-0.5 max-w-[56%] text-[22px] font-semibold leading-tight text-fg">
                {title}
              </DialogPrimitive.Title>
              {soft.sub ? (
                <p className="max-w-[56%] break-words text-[13px] text-fg-muted">{soft.sub}</p>
              ) : null}
              {soft.art ? (
                <div
                  className="pointer-events-none absolute right-16 top-1 select-none"
                  aria-hidden="true"
                >
                  {soft.art}
                </div>
              ) : null}
              <div className="absolute right-4 top-4 flex items-center gap-1">
                {headerExtra}
                <DialogPrimitive.Close
                  className="flex h-8 w-8 items-center justify-center rounded-full text-fg-muted hover:bg-bg-elevated/60 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </DialogPrimitive.Close>
              </div>
            </div>
          ) : eyebrow ? (
            <div className="sticky top-0 z-10 flex items-start justify-between gap-3 bg-bg-elevated px-5 pt-5">
              <DialogPrimitive.Title className="text-xs font-semibold uppercase tracking-[0.14em] text-fg-subtle">
                {title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Close
                className="flex items-center gap-1 rounded-md px-1 text-sm text-fg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden="true" />
                Close
              </DialogPrimitive.Close>
            </div>
          ) : (
            <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-bg-elevated px-4 py-3">
              <div className="flex min-w-0 flex-col gap-0.5">
                <DialogPrimitive.Title className="text-sm font-semibold text-fg">
                  {title}
                </DialogPrimitive.Title>
                {description ? (
                  <DialogPrimitive.Description className="text-xs text-fg-muted">
                    {description}
                  </DialogPrimitive.Description>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {headerExtra}
                <DialogPrimitive.Close
                  className="flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </DialogPrimitive.Close>
              </div>
            </div>
          )}
          <div className="flex-1" data-testid={testid ? `${testid}-body` : undefined}>
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
